import { useEffect, useState } from "react";
import { Box, Button, Checkbox, Dialog, makeStyles } from "@material-ui/core";
import useKitApi, { Kit, KitCompletedOrderRecord } from "../../api/KitApi";
import DateInputField, { getCurrentDate } from "../ui/DateInputField";
import useOrderApi, { OrderedKit } from "../../api/OrderApi";

interface Props {
  customerId: number;
  open: boolean;
  onCleanupConfirmed: (messages: string[]) => void;
  onCleanupCanceled: () => void;
}

interface RemovableKit {
  id: number;
  name: string;
  latestOrderCompletionDate: string|null;
  isUsedInAnyOrder: boolean;
}

const useStyles = makeStyles(theme => ({
  button: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    minWidth: 640
  }
}));

const KitsCleanupDialog = (props: Props) => {
  const classes = useStyles();
  const orderApi = useOrderApi();
  const kitApi = useKitApi();
  const [latestOrderCompletionDate, setLatestOrderCompletionDate] = useState<string>();
  const [kits, setKits] = useState<Kit[]>();
  const [kitCompletedOrders, setKitCompletedOrders] = useState<KitCompletedOrderRecord[]>();
  const [activeOrderedKits, setActiveOrderedKits] = useState<OrderedKit[]>();
  const [removableKits, setRemovableKits] = useState<RemovableKit[]>();
  const [selectedKitIds, setSelectedKitIds] = useState<number[]>();
  const customerId = props.customerId;

  const handleKitSelected = (selectedKitId: number, checked: boolean) => {
    setSelectedKitIds(
      checked === true
        ? selectedKitIds?.concat(selectedKitId)
        : selectedKitIds?.filter(kitId => kitId !== selectedKitId))
  };

  const handleRemoveButtonClicked = async () => {
    const messages = [];
    for (let kitId of selectedKitIds || []) {
      try {
        await kitApi.deleteKit(customerId, 0, kitId); // TODO: hardcoded 0 as priceListId
        messages.push(`Usunięto komplet o identyfikatorze ${kitId}`);
      } catch (exception) {
        messages.push(`Napotkano PROBLEM podczas usuwania kompletu o identyfikatorze ${kitId}`);
      }
    }
    props.onCleanupConfirmed(messages);
  };

  const determineRemovableKits = (
    kits: Kit[],
    kitCompletedOrders: KitCompletedOrderRecord[],
    activeOrderedKits: OrderedKit[]
  ): RemovableKit[] => {
    const removableKits: RemovableKit[] = kitCompletedOrders.map(orderKit => ({
      id: orderKit.kitId,
      name: orderKit.name,
      latestOrderCompletionDate: orderKit.latestCompletionDate || null,
      isUsedInAnyOrder: true
    }));
    const removableKitIds = removableKits.map(kit => kit.id);
    kits.forEach(kit => {
      const kitId = kit.id;
      if (!removableKitIds.includes(kitId)) {
        removableKits.push({
          id: kitId,
          name: kit.name,
          latestOrderCompletionDate: null,
          isUsedInAnyOrder: false
        });
      }
    });
    const activeOrderedKitIds = activeOrderedKits.map(orderedKit => orderedKit.kit.id);
    return removableKits
      .filter(kit => latestOrderCompletionDate === undefined
          || (kit.latestOrderCompletionDate !== undefined && kit.latestOrderCompletionDate !== null && kit.latestOrderCompletionDate <= latestOrderCompletionDate)
          || !kit.isUsedInAnyOrder)
      .filter(kit => !activeOrderedKitIds.includes(kit.id));
  };

  useEffect(() => {
    if (props.open === true) {
      kitApi
        .fetchKits(customerId, 0) // TODO: hardcoded 0 as priceListId
        .then(setKits);
      kitApi
        .fetchKitCompletedOrders(customerId)
        .then(setKitCompletedOrders);
      orderApi
        .fetchActiveOrderedKits(customerId)
        .then(setActiveOrderedKits);
    }
  }, [props.open]);

  useEffect(() => {
    if (kits !== undefined && kitCompletedOrders !== undefined && activeOrderedKits !== undefined) {
      const removableKits = determineRemovableKits(kits, kitCompletedOrders, activeOrderedKits);
      setRemovableKits(removableKits);
      setSelectedKitIds(removableKits.map(kit => kit.id));
    }
  }, [kits, kitCompletedOrders, activeOrderedKits, latestOrderCompletionDate]);

  return (
    <Dialog open={ props.open } maxWidth={ false }>
      <Box className={ classes.dialogContent }>
        <DateInputField
          initialValue={ getCurrentDate() }
          label='Data realizacji najnowszego zamówienia'
          onValueChange={ setLatestOrderCompletionDate }
        />
        { removableKits === undefined
          ? <div>...</div>
          : removableKits
              .map(removableKit =>
                <div>
                  <Checkbox
                    checked={ selectedKitIds?.includes(removableKit.id) || false }
                    onChange={ (event, checked) => handleKitSelected(removableKit.id, checked) }/>
                  { `${removableKit.name}
                   : ${removableKit.isUsedInAnyOrder ? removableKit.latestOrderCompletionDate : ' dotychczas niewykorzystany w zamówieniach'}` }
                </div>)
        }
      </Box>
      <Button
        color='primary'
        className={ classes.button }
        onClick={ handleRemoveButtonClicked }
        variant='contained'>
          Usuń
      </Button>
      <Button
        color='secondary'
        className={ classes.button }
        onClick={ props.onCleanupCanceled }
        variant='contained'>
          Anuluj
      </Button>
    </Dialog>
  );
}

export default KitsCleanupDialog;